/**
 * Shared pure transaction-breakdown helper for R27.
 * Mirrors `backend/src/utils/financialAmounts.ts`. No React/DOM/state.
 * @ticket R27-M
 */
import type { TransactionCategory, TransactionItem } from './types';

export type AllocationState = 'balanced' | 'unallocated' | 'overallocated' | 'incomplete';

export interface ItemBreakdown { gross: number | null; discount: number | null; net: number | null; quantity: number | null; complete: boolean; }

export interface TransactionBreakdown {
  transactionGross: number | null;
  transactionDiscount: number | null;
  transactionNet: number | null;
  itemGross: number | null;
  itemDiscount: number | null;
  itemNet: number | null;
  allocationDelta: number | null;
  allocationState: AllocationState;
  itemCount: number;
}

export type TransactionItemInput = Partial<TransactionItem> & { categoryRef?: TransactionCategory; };

/** Currency tolerance (in the same units as the inputs) for `'balanced'`. */
export const BALANCED_TOLERANCE = 0.01;

const EMPTY_ITEM: TransactionItemInput = Object.freeze({}) as TransactionItemInput;
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const optionalDiscount = (v: unknown): number | null =>
  v === undefined || v === null ? 0 : isFiniteNumber(v) ? Math.max(0, v) : null;
const resolveQuantity = (raw: unknown, amountValid: boolean): number | null =>
  raw === undefined || raw === null ? (amountValid ? 1 : null) : isFiniteNumber(raw) && raw >= 1 ? raw : null;

export function normalizeTransactionItems(rawItems: unknown): TransactionItemInput[] {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.map((entry): TransactionItemInput => {
    if (!entry || typeof entry !== 'object') return EMPTY_ITEM;
    const source = entry as TransactionItemInput;
    const ref = source.categoryRef;
    const refId = ref?._id ?? ref?.id;
    return {
      ...source,
      category: source.category ?? refId ?? ref?.name,
      categoryId: source.categoryId ?? refId,
      categoryName: source.categoryName ?? ref?.name,
      categoryColor: source.categoryColor ?? ref?.color,
      categoryEmoji: source.categoryEmoji ?? ref?.emoji,
    };
  });
}

export function getItemBreakdown(item: TransactionItemInput): ItemBreakdown {
  if (!item || typeof item !== 'object') return { gross: null, discount: null, net: null, quantity: null, complete: false };
  const amount = isFiniteNumber(item.amount) ? item.amount : null;
  const discount = optionalDiscount(item.discountAmount);
  const quantity = resolveQuantity(item.quantity, amount !== null);
  const gross = amount !== null && quantity !== null ? amount * quantity : null;
  const net = gross !== null && discount !== null ? Math.max(0, gross - discount) : null;
  return { gross, discount, net, quantity, complete: gross !== null && discount !== null && net !== null };
}

export function buildTransactionBreakdown(input: { amount: unknown; transactionDiscountAmount: unknown; items: ReadonlyArray<TransactionItemInput>; }): TransactionBreakdown {
  const amount = isFiniteNumber(input.amount) ? input.amount : null;
  const gross = amount !== null ? Math.abs(amount) : null;
  const discount = optionalDiscount(input.transactionDiscountAmount);
  const net = gross !== null && discount !== null ? Math.max(0, gross - discount) : null;
  const items = Array.isArray(input.items) ? input.items : [];
  const breakdowns = items.map(getItemBreakdown);
  const allItemsComplete = items.length > 0 && breakdowns.every((entry) => entry.complete);
  const itemGross = allItemsComplete ? breakdowns.reduce((s, e) => s + (e.gross ?? 0), 0) : null;
  const itemDiscount = allItemsComplete ? breakdowns.reduce((s, e) => s + (e.discount ?? 0), 0) : null;
  const itemNet = allItemsComplete ? breakdowns.reduce((s, e) => s + (e.net ?? 0), 0) : null;
  let allocationDelta: number | null = null;
  let allocationState: AllocationState = 'incomplete';
  if (net !== null && allItemsComplete && itemNet !== null) {
    allocationDelta = +(net - itemNet).toFixed(2);
    const abs = Math.abs(allocationDelta);
    if (abs <= BALANCED_TOLERANCE) allocationState = 'balanced';
    else if (allocationDelta > 0) allocationState = 'unallocated';
    else allocationState = 'overallocated';
  }
  return { transactionGross: gross, transactionDiscount: discount, transactionNet: net, itemGross, itemDiscount, itemNet, allocationDelta, allocationState, itemCount: items.length };
}
